import { useEffect, useState } from "react";

interface LightboxProps {
  images: string[];
  title: string;
  initialIndex?: number;
  onClose: () => void;
}

export default function Lightbox({ images, title, initialIndex = 0, onClose }: LightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") setIndex((prev) => (prev + 1) % images.length);
      if (event.key === "ArrowLeft") setIndex((prev) => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [images.length, onClose]);

  if (images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-primary-950/95 p-4 md:p-8 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} 剧照预览`}
    >
      <div className="flex items-center justify-between gap-4 text-background-50">
        <div>
          <p className="font-heading text-sm md:text-base">{title}</p>
          <p className="mt-1 text-[11px] text-background-50/60">
            第 {index + 1} / {images.length} 张 · 使用方向键切换，Esc 关闭
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭预览"
          className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-50/25 text-xl transition-colors hover:border-accent-400 hover:text-accent-300"
        >
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      </div>

      <div className="relative mt-4 flex min-h-0 flex-1 items-center justify-center">
        <button
          type="button"
          onClick={() => setIndex((prev) => (prev - 1 + images.length) % images.length)}
          aria-label="上一张"
          className="absolute left-0 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/25 bg-primary-950/60 text-xl text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300 md:h-12 md:w-12"
        >
          <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
        </button>
        <img
          src={images[index]}
          alt={`${title} 剧照 ${index + 1}`}
          className="max-h-full max-w-full rounded-md object-contain"
        />
        <button
          type="button"
          onClick={() => setIndex((prev) => (prev + 1) % images.length)}
          aria-label="下一张"
          className="absolute right-0 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/25 bg-primary-950/60 text-xl text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300 md:h-12 md:w-12"
        >
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        </button>
      </div>

      <div className="mt-4 flex justify-center gap-2 overflow-x-auto pb-1 hide-scrollbar">
        {images.map((image, imageIndex) => (
          <button
            key={image}
            type="button"
            onClick={() => setIndex(imageIndex)}
            aria-label={`查看第 ${imageIndex + 1} 张`}
            className={`h-12 w-20 shrink-0 cursor-pointer overflow-hidden rounded border transition-all ${
              imageIndex === index ? "border-accent-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"
            }`}
          >
            <img src={image} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}